import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { Lines } from "@/components/marketing/Lines";
import { BODY, HEADING_3 } from "@/components/marketing/marketingType";
import { SECTION_PANEL } from "@/components/marketing/sectionPanel";
import {
  MID,
  NOT_WIDE,
  TINY,
  WIDE,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Card } from "@/content/landing/types";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import type { Cards } from "./cardsShared";
import { sectionPad } from "./landingRun";

/** `.main_header_box.is-green`, `.is-purple` and `.is-purple-blue`, in the rows' order. */
const ROW_GROUNDS = [
  marketingPalette.sectionMint,
  marketingPalette.sectionLilac,
  marketingPalette.sectionPeriwinkle,
] as const;

/**
 * The box each row is set in, stacked below the wide step: the green one
 * closes to 16 there, and the lilac one, which leads with its art, puts its
 * words first.
 */
function rowBox(index: number): SxProps<Theme> {
  const tone = index % ROW_GROUNDS.length;
  return [
    SECTION_PANEL,
    {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "32px",
      backgroundColor: ROW_GROUNDS[tone],
      [NOT_WIDE]: {
        flexDirection: tone === 1 ? "column-reverse" : "column",
        ...(tone === 0 ? { gap: "16px", justifyContent: "flex-start" } : {}),
      },
    },
  ];
}

/** `.ppc-card-left`: the title, its line, the dotted points and the button, on 28rem. */
function RowCopy({ card }: { card: Card }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "12px",
        width: "448px",
        [NOT_WIDE]: { width: 1 },
      }}
    >
      <Typography component="h3" sx={HEADING_3}>
        <Lines text={card.title} />
      </Typography>
      {card.body ? <Typography sx={BODY}>{card.body}</Typography> : null}
      {card.points?.length ? (
        <Box component="ul" sx={{ m: 0, mb: "10px", pl: "23px" }}>
          {card.points.map((point) => (
            <Box
              component="li"
              key={point}
              // The site's every `li` closes on .3rem.
              sx={{ mb: "4.8px", color: marketingPalette.ink, fontWeight: 500 }}
            >
              {point}
            </Box>
          ))}
        </Box>
      ) : null}
      {card.cta ? (
        <Box
          sx={{
            width: "224px",
            "& > a": { width: 1 },
            [MID]: { width: 1 },
          }}
        >
          <LandingButton cta={card.cta} variant="primary" />
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * `.ppc-card-visual`: the picture in a square, 22rem, 80% stacked, the box's
 * width on a phone. It shrinks with the words where the row is a little too
 * narrow for both, just above the wide step.
 */
function RowArt({ src }: { src: string }) {
  return (
    <Box
      sx={{
        width: "352px",
        [NOT_WIDE]: { width: "80%" },
        [MID]: { width: 1 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          pt: "100%",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <LandingImage
          src={src}
          sx={{
            position: "absolute",
            inset: 0,
            width: 1,
            height: 1,
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * `.c-cloneable.is-ppc-prod`: the product rows, each a box beside its picture
 * in turn, 48 apart, that stack as the page scrolls above the wide step, each
 * held 32 under the one before. The third card is laid on white.
 */
export default function ProductRows({ block }: { block: Cards }) {
  return (
    <LandingSection
      padding={block.pad ? sectionPad(block.pad) : "none"}
      sx={{ maxWidth: "1152px", px: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "48px",
          px: "36px",
          [TINY]: { px: "16px" },
        }}
      >
        {block.items.map((card, index) => (
          <Box
            key={card.title}
            sx={{
              position: "relative",
              ...(index === 2
                ? { backgroundColor: "common.white", borderRadius: "16px" }
                : {}),
              [WIDE]: { position: "sticky", top: `${96 + 32 * index}px` },
            }}
          >
            <Box sx={rowBox(index)}>
              {index % 2 === 1 && card.image ? (
                <RowArt src={card.image} />
              ) : null}
              <RowCopy card={card} />
              {index % 2 === 0 && card.image ? (
                <RowArt src={card.image} />
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </LandingSection>
  );
}
